import {
  abbreviateId,
  Box,
  CopyButton,
  Divider,
  Flex,
  formattedDate,
  HStack,
  InlineFormattedNumber,
  Text,
  TransferStatus,
  TransferStatusEnum,
  useMarketAccountsContext,
  useMarketsContext,
  type Transfer,
} from '@talos/kyoko';

import { DetailsRow } from '@src/components/DetailsDrawer/Customer/CustomerTransferDetailsDrawer/TransferOverviewTab/DetailsRow';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';

import { TransferConfirmationCard } from './TransferConfirmationCard';

interface TransferCompletedContentProps {
  transfer: Transfer;
  onResubmit: () => void;
}

const TX_HASH_VISIBLE_CHARS = 8;

function getTransferTxHash(transfer: Transfer): string | undefined {
  return transfer.TxHash ?? transfer.TxHashes?.[0];
}

function truncateTxHash(txHash: string): string {
  if (txHash.length <= TX_HASH_VISIBLE_CHARS) {
    return txHash;
  }
  return `${txHash.slice(0, TX_HASH_VISIBLE_CHARS)}*`;
}

export function TransferCompletedContent({ transfer, onResubmit }: TransferCompletedContentProps) {
  const { marketAccountsByID } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { enableAccountSegregation } = useFeatureFlag();
  const avoidMarketIdDisplay = enableAccountSegregation ?? false;
  const isCompleted = transfer.Status === TransferStatusEnum.Completed;
  const isRejected = transfer.Status === TransferStatusEnum.Rejected;
  const rejectReason = isRejected && transfer.Text ? transfer.Text : undefined;
  const txHash = isCompleted ? getTransferTxHash(transfer) : undefined;
  const transferId = isCompleted && transfer.TransferID ? abbreviateId(transfer.TransferID) : undefined;
  const providerDisplayName = marketsByName.get(transfer.Market)?.DisplayName ?? transfer.Market;
  const timestamp = formattedDate(transfer.SubmitTime ?? transfer.TransactTime, '{yyyy}-{MM}-{dd} {HH}:{mm}:{ss}');

  return (
    <TransferConfirmationCard onResubmit={onResubmit}>
      <Flex flexDirection="column" gap="spacing4">
        <Flex justifyContent="space-between" alignItems="center" gap="spacing12">
          <Text size="fontSizeDefault" color="colorTextSubtle" data-testid="transfer-confirmation-amount">
            Send{' '}
            <Text color="colorTextImportant">
              <InlineFormattedNumber number={transfer.Amount} currency={transfer.Currency} showCurrency={false} />
            </Text>{' '}
            {transfer.Currency}
          </Text>
          <Box data-testid="transfer-confirmation-result-status">
            <TransferStatus status={transfer.Status} align="right" />
          </Box>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center" gap="spacing12">
          <Text color="colorTextDefault" data-testid="transfer-confirmation-timestamp">
            {timestamp}
          </Text>
          <Text data-testid="transfer-confirmation-id">
            <Text color="colorTextSubtle">ID </Text>
            <Text color={transferId != null ? 'colorTextImportant' : 'colorTextSubtle'}>{transferId ?? '–'}</Text>
          </Text>
        </Flex>
        {rejectReason != null && (
          <Text color="colorTextWarning" data-testid="transfer-confirmation-reject-reason">
            {rejectReason}
          </Text>
        )}
      </Flex>

      <Divider color="gray.010" />

      <Flex flexDirection="column" gap="spacing12">
        <DetailsRow title="Provider" color="colorTextSubtle">
          <Text color="colorTextImportant" data-testid="transfer-confirmation-provider">
            {providerDisplayName}
          </Text>
        </DetailsRow>
        <DetailsRow title="Source" color="colorTextSubtle">
          <Text color="colorTextImportant" data-testid="transfer-confirmation-source">
            {transfer.getAccountDisplay(marketAccountsByID, 'source', avoidMarketIdDisplay)}
          </Text>
        </DetailsRow>
        <DetailsRow title="Destination" color="colorTextSubtle">
          <Text color="colorTextImportant" data-testid="transfer-confirmation-destination">
            {transfer.getAccountDisplay(marketAccountsByID, 'destination', avoidMarketIdDisplay)}
          </Text>
        </DetailsRow>
      </Flex>

      {txHash != null && (
        <>
          <Divider color="gray.010" />
          <DetailsRow title="TX Hash" color="colorTextSubtle" whiteSpace="nowrap">
            <HStack gap="spacing4" maxWidth="100%">
              <CopyButton value={txHash} />
              <Text
                color="colorTextImportant"
                overflow="hidden"
                textOverflow="ellipsis"
                title={txHash}
                whiteSpace="nowrap"
                data-testid="transfer-confirmation-tx-hash"
              >
                {truncateTxHash(txHash)}
              </Text>
            </HStack>
          </DetailsRow>
        </>
      )}
    </TransferConfirmationCard>
  );
}
